<template>
  <div ref="rootRef" :dir="isRtl != false ? 'rtl' : 'ltr'" class="relative inline-block custom-dropdown-selector-container text-left" :class="[$attrs.class]">
    <button
      type="button"
      class="btn relative w-full justify-start font-normal text-left h-auto py-2 px-3 min-h-0 bg-base-100 border-base-300 hover:border-base-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      :class="buttonClass"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen !== false"
      :disabled="disabled !== false"
    >
      <div
        v-if="multiple !== true && toneLike === true && selectedOption !== null"
        class="flex items-center w-full min-w-0"
        style="padding-inline-end: 1.75rem;"
      >
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0" style="margin-inline-end: 0.5rem;">
          <img
            v-if="selectedOption.imageUrl !== undefined && selectedOption.imageUrl !== null && selectedOption.imageUrl !== ''"
            :src="selectedOption.imageUrl"
            alt=""
            class="w-4 h-4 object-contain"
          />
          <Icon v-else :key="'selected-icon-' + String(selectedOption.value) + '-' + String(selectedOption.icon)" :icon="selectedOption.icon" class="w-4 h-4 text-base-content" />
        </span>
        <div class="flex-1 min-w-0 overflow-hidden relative" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
          <span class="block text-sm font-medium truncate relative w-full">{{ selectedOption.label }}</span>
          <span v-if="selectedOption.description !== undefined && selectedOption.description !== null && selectedOption.description !== ''" class="block text-xs text-base-content opacity-70 truncate">{{ selectedOption.description }}</span>
        </div>
      </div>
      <div
        v-else-if="multiple !== true && toneLike === true"
        class="flex items-center w-full min-w-0"
        style="padding-inline-end: 1.75rem;"
      >
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0" style="margin-inline-end: 0.5rem;">
          <Icon icon="heroicons:squares-2x2-20-solid" class="w-4 h-4 text-base-content" />
        </span>
        <span class="text-sm text-base-content opacity-70 whitespace-nowrap">{{ placeholder }}</span>
      </div>
      <div
        v-else-if="multiple !== true"
        class="flex min-w-0 w-full items-center justify-start"
        :class="isRtl === true ? 'pl-8 pr-0' : 'pr-8 pl-0'"
      >
        <span
          v-if="selectedOption !== null && selectedOption.icon !== undefined && selectedOption.icon !== null && selectedOption.icon !== ''"
          class="inline-flex flex-shrink-0 items-center justify-center text-base-content"
          :class="[toneLike === true ? 'h-7 w-7 rounded-md bg-base-300 dark:bg-gray-700' : 'h-4 w-4', isRtl === true ? 'ml-2' : 'mr-2']"
        >
          <Icon :key="'selected-icon-' + String(selectedOption.value) + '-' + String(selectedOption.icon)" :icon="selectedOption.icon" class="h-4 w-4" />
        </span>
        <span class="block min-w-0 truncate" :class="[isRtl === true ? 'text-right' : 'text-left', toneLike === true ? 'text-sm font-medium' : '']">
          {{ selectedOption !== null ? selectedOption.label : placeholder }}
        </span>
      </div>
      <div
        v-else
        class="flex min-w-0 flex-1 items-center truncate"
        :class="isRtl === true ? 'pl-8 pr-0' : 'pr-8 pl-0'"
      >
        <span class="block truncate w-full" :class="isRtl === true ? 'text-right' : 'text-left'">
          {{ selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder }}
        </span>
      </div>
      <Icon icon="heroicons:chevron-down-20-solid" :class="[toneLike === true ? 'w-5 h-5' : 'w-4 h-4', 'text-base-content opacity-60 flex-shrink-0 absolute', isRtl === true ? (toneLike === true ? 'left-3' : 'left-2') : (toneLike === true ? 'right-3' : 'right-2')]" style="top: 50%; transform: translateY(-50%);" />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen === true"
        class="bg-base-100 shadow-lg rounded-md border border-base-300 dropdown-options-list overflow-y-auto z-[10050]"
        :style="dropdownPanelStyle"
        ref="dropdownListRef"
        role="listbox"
      >
        <div :class="toneLike === true ? 'py-1 space-y-1' : 'py-1'">
          <div
            v-for="opt in options"
            :key="String(opt.value) + '-' + String(opt.icon !== undefined ? opt.icon : '')"
            :class="toneLike === true ? 'card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out' : 'px-3 py-2 cursor-pointer transition-colors duration-150 flex items-center justify-between'"
            :style="toneLike === true ? '' : undefined"
            @click.stop="selectOption(opt.value)"
          >
            <div
              v-if="toneLike === true"
              :class="{
                'bg-primary text-primary-content': isSelected(opt.value) !== false,
                'hover:bg-base-200 dark:hover:bg-gray-700': isSelected(opt.value) === false
              }"
              class="p-3 rounded-md"
            >
              <div class="flex items-start" :class="isRtl === true ? 'flex-row-reverse' : ''">
                <div class="flex-shrink-0">
                  <span class="inline-flex justify-center items-center w-8 h-8 rounded-lg" :class="isSelected(opt.value) !== false ? 'bg-primary-content/15' : 'bg-base-300 dark:bg-gray-600'">
                    <img
                      v-if="opt.imageUrl !== undefined && opt.imageUrl !== null && opt.imageUrl !== ''"
                      :src="opt.imageUrl"
                      alt=""
                      class="w-5 h-5 object-contain"
                    />
                    <Icon v-else :key="'option-icon-' + String(opt.value) + '-' + String(opt.icon)" :icon="opt.icon" class="w-5 h-5" :class="isSelected(opt.value) !== false ? 'text-primary-content' : 'text-base-content'" />
                  </span>
                </div>
                <div class="flex-grow overflow-hidden relative w-full" :class="[isRtl === true ? 'text-right mr-4' : 'text-left ml-4']">
                  <h3 class="text-sm font-semibold leading-tight" :class="isSelected(opt.value) !== false ? 'text-primary-content' : 'text-base-content'">{{ opt.label }}</h3>
                  <p v-if="opt.description !== undefined && opt.description !== null && opt.description !== ''" class="text-xs mt-0.5" :class="isSelected(opt.value) !== false ? 'text-primary-content opacity-80' : 'text-base-content opacity-70'">
                    {{ opt.description }}
                  </p>
                </div>
                <div v-if="multiple === true" class="flex-shrink-0" :class="isRtl === true ? 'mr-3' : 'ml-3'">
                  <Icon v-if="isSelected(opt.value) !== false" icon="heroicons:check-20-solid" class="w-5 h-5 text-primary-content" />
                </div>
              </div>
            </div>
            <div
              v-else
              class="flex items-center justify-between"
              :class="{ 
                'bg-primary text-primary-content': isSelected(opt.value) !== false,
                'hover:bg-base-200': isSelected(opt.value) === false
              }"
            >
              <div class="flex items-center truncate">
                <span
                  v-if="opt.icon !== undefined && opt.icon !== null && opt.icon !== ''"
                  class="inline-flex flex-shrink-0 items-center justify-center text-base-content"
                  :class="[toneLike === true ? 'h-8 w-8 rounded-md bg-base-300 dark:bg-gray-600' : 'h-4 w-4', isSelected(opt.value) !== false ? (toneLike === true ? 'bg-primary-content/15 text-primary-content' : 'text-primary-content') : (toneLike === true ? 'text-base-content' : 'text-base-content')]"
                  :style="isRtl === true ? { marginLeft: '0.75rem' } : { marginRight: '0.75rem' }"
                >
                  <img
                    v-if="opt.imageUrl !== undefined && opt.imageUrl !== null && opt.imageUrl !== ''"
                    :src="opt.imageUrl"
                    alt=""
                    class="w-4 h-4 object-contain"
                  />
                  <Icon v-else :key="'option-icon-' + String(opt.value) + '-' + String(opt.icon)" :icon="opt.icon" class="w-4 h-4" />
                </span>
                <span class="block truncate" :class="[isRtl === true ? 'text-right' : 'text-left', toneLike === true ? 'text-sm font-medium' : '']">{{ opt.label }}</span>
              </div>
              <Icon v-if="multiple === true && isSelected(opt.value) !== false" icon="heroicons:check-20-solid" class="w-4 h-4 flex-shrink-0" />
            </div>
          </div>
          <div v-if="options.length === 0" class="px-3 py-2 text-sm text-base-content opacity-50 text-center">
            No options available
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from "@iconify/vue/dist/offline";
import { useI18n } from '@/i18n';
import { useBodyAnchoredDropdown } from '@/composables/useBodyAnchoredDropdown';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object, Array, null],
    default: null
  },
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select an option'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  buttonClass: {
    type: String,
    default: ''
  },
  toneLike: {
    type: Boolean,
    default: false
  },
  dropdownStyle: {
    type: String,
    default: 'max-height: 250px; overflow-y: auto;'
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const isOpen = ref(false);
const rootRef = ref(null);
const dropdownListRef = ref(null);
const { currentLanguage } = useI18n();
const isRtl = computed(() => (['he','ar'].includes(currentLanguage.value) === true) ? true : false);

function parseMaxHeightCapFromString(style) {
  if (typeof style !== 'string' || style.length === 0) {
    return 250;
  }
  const m = /max-height:\s*(\d+(?:\.\d+)?)px/i.exec(style);
  if (m === null) {
    return 250;
  }
  return Math.min(500, Math.max(80, Math.round(parseFloat(m[1]))));
}

const listCapHeight = computed(() => parseMaxHeightCapFromString(props.dropdownStyle));

const { panelStyle } = useBodyAnchoredDropdown(rootRef, isOpen, {
  capHeight: 250,
  resolveCapHeight: () => listCapHeight.value,
  zIndex: 10050,
  minHeight: 80,
  panelRef: dropdownListRef
});

const dropdownPanelStyle = computed(() => {
  const base = panelStyle.value;
  if (typeof props.dropdownStyle === 'string' && props.dropdownStyle.length > 0) {
    return [props.dropdownStyle, base];
  }
  return base;
});

const isSelected = (val) => {
  if (props.multiple === true) {
    return Array.isArray(props.modelValue) === true && props.modelValue.includes(val) === true;
  }
  return props.modelValue === val;
};

const selectedOption = computed(() => {
  if (props.multiple === true) return null;
  const found = props.options.find(opt => opt.value === props.modelValue);
  return found !== undefined ? found : null;
});

const selectedLabels = computed(() => {
  if (props.multiple !== true || Array.isArray(props.modelValue) === false) return [];
  return props.modelValue.map(val => {
    const found = props.options.find(opt => opt.value === val);
    return found !== undefined ? found.label : String(val);
  });
});

const closeDropdown = () => {
  isOpen.value = false;
};

const toggleDropdown = () => {
  if (props.disabled !== true) {
    if (isOpen.value === true) {
      closeDropdown();
    } else {
      isOpen.value = true;
    }
  }
};

const selectOption = (val) => {
  if (props.multiple === true) {
    let newValue = Array.isArray(props.modelValue) === true ? [...props.modelValue] : [];
    if (newValue.includes(val) === true) {
      newValue = newValue.filter(v => v !== val);
    } else {
      newValue.push(val);
    }
    emit('update:modelValue', newValue);
    emit('change', newValue);
    return;
  }
  emit('update:modelValue', val);
  emit('change', val);
  closeDropdown();
};

const handleClickOutside = (event) => {
  if (isOpen.value !== true) {
    return;
  }
  const rootElement = rootRef.value;
  const menuElement = dropdownListRef.value;
  if (rootElement !== null && rootElement !== undefined && rootElement.contains(event.target) === true) {
    return;
  }
  if (menuElement !== null && menuElement !== undefined && menuElement.contains(event.target) === true) {
    return;
  }
  closeDropdown();
};

watch(() => props.modelValue, () => {
  if (props.multiple !== true && isOpen.value === true) {
    closeDropdown();
  }
});

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>

<style scoped>
.dropdown-options-list {
  scrollbar-width: thin;
}
</style>